import React, { ReactNode, createContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'

import { useApolloErrorHandler, useApolloErrorModal } from '@acorns/web-utils'
import { useLazyQuery, useMutation } from '@apollo/react-hooks'
import createEnvironment from 'config/env'
import { LocationState } from 'history'

import { APP_VERSION, VISITED_PAGES } from 'app-constants'
import platform from 'components/Trackers/platform'
import {
  ActionType,
  FunnelMetadata,
  FunnelName,
  PageName,
  RegisterProspectResult,
  RegistrationFunnel,
  RegistrationProminenceBannerContent,
  SourceAttributes,
} from 'generated/types'

import { updateThemeColor } from 'theme'
import trackEvent from 'utils/analytics/trackEvent'
import { sendToSentry } from 'utils/errors'
import {
  DEFAULT_FUNNEL_NAME,
  getFlow,
  getFunnelName,
  getVisitedPages,
  isRegistrationFlow,
} from 'utils/FlowProvider/utils'
import { getGeneralError } from 'utils/getGeneralError'
import { getTierPrice } from 'utils/TieredRegistration/utils'

import registerProspectMutation from './gql/registerProspect.mutation.gql'
import registrationFunnelQuery from './gql/registrationFunnel.query.gql'
import { PageToRoute } from './types'

const VALID_FUNNEL_TYPENAME = 'RegistrationFunnel'
const FUNNEL_KEY = 'FUNNEL'
export const IS_DYNAMIC_FUNNEL_ENABLED = 'IS_DYNAMIC_FUNNEL_ENABLED'
export const METADATA_KEY = 'FUNNEL_METADATA'
const SOURCE_ATTRIBUTES_KEY = 'FUNNEL_SOURCE_ATTRIBUTES'
const VISITED_KEY = 'FUNNEL_VISITED_PAGES'

export interface FunnelContextProps {
  metadata: FunnelMetadata
  registrationFunnel: (userId: string) => void
  registerProspect: (email: string, sourceId: string, rawJson: string) => Promise<void>
  isDynamicFunnelEnabled: boolean
  routeToPage: (page: PageName, action: ActionType, locationState?: Record<string, any>) => void
  hasVisited(page: PageName): boolean
  isBackEnabled(page: PageName): boolean
  getContent(page: PageName): RegistrationProminenceBannerContent
  sourceAttributes: SourceAttributes
}

type FunnelProviderProps = {
  children?: ReactNode
}

type RegistrationFlow = {
  [key in PageName]?: RegistrationPageActions
}

type RegistrationPageActions = {
  isBackEnabled: boolean
  content: RegistrationProminenceBannerContent
} & { [key in ActionType]: PageName }

interface RegistrationFunnelQueryResult {
  registrationFunnel: RegistrationFunnel
}

interface RegisterProspectMutationResult {
  registerProspect: RegisterProspectResult
}

interface RegistrationFunnelInput {
  userId: string
}

interface RegisterProspectInput {
  email: string
  sourceId: string
  rawJson: string
}

// Set up environment for dynamic funnels
const environment = createEnvironment()
const dynamicFunnels = environment.get('dynamicFunnels').split(',')
const validFunnels = Object.values(FunnelName)
if (dynamicFunnels.length > 1 || (dynamicFunnels.length === 1 && dynamicFunnels[0] !== '')) {
  dynamicFunnels.forEach((funnel) => {
    if (!validFunnels.includes(funnel)) {
      const err = new Error(
        `Dynamic Funnels value contains invalid funnel name ${funnel}. See 'FunnelName' enum for correct values.`,
      )
      sendToSentry(err)
      throw err
    }
  })
}

export const FunnelContext = createContext<FunnelContextProps>(null)

export const FunnelProvider = ({ children }: FunnelProviderProps) => {
  const { closeErrorModal, showErrorModal } = useApolloErrorModal()
  const [isEnabled, setIsEnabled] = useState<boolean>(false)
  const flow = useRef<RegistrationFlow>(null)
  const history = useHistory()
  const [metadata, setMetadata] = useState<FunnelMetadata>(null)
  const [sourceAttributes, setSourceAttributes] = useState<SourceAttributes>(null)
  const visitedPages = useRef(new Set())

  const [registrationFunnel] = useLazyQuery<RegistrationFunnelQueryResult, RegistrationFunnelInput>(
    registrationFunnelQuery,
    {
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      onCompleted: (response) => updateState(response.registrationFunnel, 'registrationFunnel'),
    },
  )
  const [registerProspect] = useMutation<RegisterProspectMutationResult, RegisterProspectInput>(
    registerProspectMutation,
  )

  const generalError = getGeneralError({}, { closeErrorModal }, undefined)
  useApolloErrorHandler('RegistrationFunnel', () => generalError)
  useApolloErrorHandler('RegisterProspect', () => generalError)

  useEffect(() => {
    flow.current = JSON.parse(window.sessionStorage.getItem(FUNNEL_KEY))
    // Need to have `parsedMetadata` in separate const because we need to access the data in order
    // to enable dynamic funnels or not; state is set asynchronously
    const parsedMetadata = JSON.parse(window.sessionStorage.getItem(METADATA_KEY))
    setMetadata(parsedMetadata)
    // Enable dynamic funnels for current funnel if the environment specifies to serve the funnel
    setIsEnabled(dynamicFunnels.includes(parsedMetadata?.name))
    visitedPages.current = new Set(JSON.parse(window.sessionStorage.getItem(VISITED_KEY)))
    setSourceAttributes(JSON.parse(window.sessionStorage.getItem(SOURCE_ATTRIBUTES_KEY)))
  }, [])

  useEffect(() => {
    window.sessionStorage.setItem(SOURCE_ATTRIBUTES_KEY, JSON.stringify(sourceAttributes))
    window.sessionStorage.setItem(METADATA_KEY, JSON.stringify(metadata))
    // Allows the 'trackEvent' function to utilize dynamic funnels capabilities outside of component scope
    window.sessionStorage.setItem(IS_DYNAMIC_FUNNEL_ENABLED, isEnabled.toString())
  }, [metadata, isEnabled])

  /**
   * Determines next page to route user to and pushes entry onto history stack.
   * @param page Name of page.
   * @param action CTA action to perform.
   * @param state State to persist at specific location.
   */
  const route = (page: PageName, action: ActionType, state?: LocationState) => {
    const currentPage = PageToRoute[page]
    const nextPage = PageToRoute[flow.current[page][action]]
    window.analytics.track('Step', {
      app_version: APP_VERSION,
      from: currentPage,
      to: nextPage,
      page,
      action,
      platform,
      funnel: metadata.name,
      funnelVersion: metadata.version,
      tierPrice: getTierPrice(),
    })

    visitedPages.current.add(nextPage)
    window.sessionStorage.setItem(VISITED_KEY, JSON.stringify([...visitedPages.current]))

    // This is for backwards compatibility with the FlowProvider
    // We need to be able to track a user's journey even if they switch funnels to something
    // not enabled dynamically
    const deprecatedVisitedPages = getVisitedPages()
    deprecatedVisitedPages.push(nextPage)
    window.localStorage.setItem(VISITED_PAGES, JSON.stringify(deprecatedVisitedPages))

    // Setting referrer field in history state allows us to know what our last page was for analytics
    state ? (state['referrer'] = currentPage) : (state = { referrer: currentPage })
    history.push(nextPage, state)
    updateThemeColor()
  }

  const updateState = (data: RegisterProspectResult, requestName: string): void => {
    if (data.__typename !== VALID_FUNNEL_TYPENAME) {
      showErrorModal(generalError)
      const err = new Error(`GraphQL request responded with type ${data.__typename}`)
      sendToSentry(err, {
        breadcrumb: `Attempting to call ${requestName} for user`,
      })
      trackEvent('Container Viewed', {
        eventData: {
          object_name: 'funnel_context.error',
          type: 'container_viewed',
          event_text: 'Container Viewed',
          screen: 'signupError',
        },
      })
      window.analytics.track('Funnel Error', {
        event: 'funnel_error',
        event_text: 'Funnel Error',
        platform,
        funnel: getFunnelName(getFlow()),
        request: requestName,
      })
      throw err
    }

    setSourceAttributes(data.sourceAttributes)
    setMetadata(data.metadata)
    setIsEnabled(dynamicFunnels.includes(data.metadata.name))
    /**
     * 'flow' has the shape:
     *   {
     *     PageName.SIGNUP: {
     *       ActionType.CONTINUE: PageName.PERSONAL_INFO
     *       isBackEnabled: true | false
     *     },
     *     ...
     *   }
     */

    flow.current = data.flow.reduce((pageMap, page) => {
      pageMap[page.name] = page.actions.reduce((actionMap, action) => {
        actionMap[action.type] = action.nextPage
        return actionMap
      }, {})
      pageMap[page.name].isBackEnabled = page.isBackEnabled

      pageMap[page.name].content = page.content

      return pageMap
    }, {})
    window.sessionStorage.setItem(FUNNEL_KEY, JSON.stringify(flow.current))
  }

  /**
   * Registers new prospective user by creating "Prospect" in registration service
   * (saves email and source attributes).
   * @param email User's email address.
   * @param sourceId User's source ID.
   * @param rawJson Source attributes.
   */
  const register = async (email: string, sourceId: string, rawJson: string): Promise<void> => {
    const response = await registerProspect({
      variables: { email, sourceId, rawJson },
    })
    return updateState(response.data.registerProspect, 'registerProspect')
  }

  const getFunnel = (userId: string): void => {
    registrationFunnel({ variables: { userId } })
  }

  const hasVisited = (page: PageName): boolean => visitedPages.current.has(PageToRoute[page])

  const isBackEnabled = (page: PageName): boolean => flow.current[page]?.isBackEnabled ?? false

  const getContent = (page: PageName) => {
    if (
      (isEnabled && metadata?.name === FunnelName.DEFAULT) ||
      (!isEnabled && getFlow() === DEFAULT_FUNNEL_NAME) ||
      (!isEnabled && getFlow() === 'investOnboarding' && isRegistrationFlow())
    ) {
      return flow.current?.[page]?.content
    }
  }

  return (
    <FunnelContext.Provider
      value={{
        metadata,
        registrationFunnel: getFunnel,
        registerProspect: register,
        isDynamicFunnelEnabled: isEnabled,
        routeToPage: route,
        hasVisited,
        isBackEnabled,
        getContent,
        sourceAttributes,
      }}
    >
      {children}
    </FunnelContext.Provider>
  )
}

export const testExports = {
  FUNNEL_KEY,
}
